// ============================================================================
// PHONE — one shared parser/formatter/validator for every phone field
// ============================================================================
//
// Four jobs, deliberately separated:
//
//   formatPhoneInput()      what the member sees WHILE typing (US grouping)
//   isPlausiblePhone()      the validation gate
//   normalizePhone()        what we STORE
//   formatPhoneForDisplay() how a stored value is rendered back for reading
//
// WHERE THE STRICTNESS LINE IS DRAWN: real signups must never be blocked, so
// the gate fails OPEN. Anything with 7–15 digits and no stray letters passes —
// including every mixed format already sitting in prod ("555-0101",
// "+15555550100", "(555) 123-4567"). Only input that clearly is not a phone
// number ("banana", an email address, three digits) is rejected.
//
// WHAT WE STORE: E.164 (+15551234567) whenever the number parses with
// certainty, otherwise the trimmed input verbatim. E.164 is unambiguous, is
// already the shape of some prod rows, and is the only form a future tel: /
// wa.me link can be built from. Anything we cannot parse is never rewritten —
// we would rather keep an odd string than destroy a member's data.

import { z } from "zod";

/** E.164 caps at 15 digits; 7 is the shortest real (local US) number. */
const MIN_DIGITS = 7;
const MAX_DIGITS = 15;

/** Trailing extension ("x12", "ext. 345") — set aside before judging the number. */
const EXTENSION_RE = /\s*(?:ext|extension|x)\.?\s*\d+\s*$/i;

/** The only characters a phone number may legitimately contain. */
const PHONE_CHARS_RE = /^[0-9+()\-.\s]*$/;

const digitsOf = (value: string): string => value.replace(/\D/g, "");

export const PHONE_ERROR_MESSAGE =
  "Enter a valid phone number, e.g. (555) 123-4567";

/**
 * Live formatting for a phone input's onChange. US-centric grouping; a leading
 * `+` switches to the international path, where country codes vary in length so
 * we keep the digits and leave the shape alone rather than guess wrong.
 *
 * Input that isn't plain phone punctuation (an extension, a pasted note) is
 * returned untouched — the validator, not the formatter, decides what is
 * acceptable, and silently eating characters someone typed is worse than an
 * unformatted field.
 */
export function formatPhoneInput(raw: string): string {
  if (!PHONE_CHARS_RE.test(raw)) return raw;

  const isInternational = raw.trimStart().startsWith("+");
  const digits = digitsOf(raw);

  if (!digits) return isInternational ? "+" : "";
  if (isInternational) return `+${digits}`;

  if (digits.length <= 3) return digits;
  // 7 digits stays local ("555-0101") — that is exactly how some existing rows
  // are stored, and regrouping them into an area code would mangle them.
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  // Longer than a US number with no leading + to name the country: leave the
  // digits ungrouped rather than invent a grouping. Never truncate.
  return digits;
}

/**
 * The validation gate. Fails open by design — see the strictness note above.
 * Empty is NOT plausible; requiredness is the caller's call, not ours.
 */
export function isPlausiblePhone(raw: string | null | undefined): boolean {
  const value = (raw ?? "").trim();
  if (!value) return false;

  const base = value.replace(EXTENSION_RE, "");
  // Letters, @, or words left over mean this isn't a phone number.
  if (!PHONE_CHARS_RE.test(base)) return false;
  // A + is only meaningful as a country-code prefix.
  if (base.includes("+") && !base.trimStart().startsWith("+")) return false;

  const digits = digitsOf(base);
  return digits.length >= MIN_DIGITS && digits.length <= MAX_DIGITS;
}

/**
 * What gets written to contact_info / profile_references. Returns null for
 * blank so callers can hand it straight to a nullable column.
 */
export function normalizePhone(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;

  const ext = value.match(EXTENSION_RE)?.[0]?.trim() ?? "";
  const base = ext ? value.replace(EXTENSION_RE, "") : value;
  if (!PHONE_CHARS_RE.test(base)) return value;

  const digits = digitsOf(base);
  let e164: string | null = null;
  if (base.trimStart().startsWith("+")) {
    if (digits.length >= 8 && digits.length <= MAX_DIGITS) e164 = `+${digits}`;
  } else if (digits.length === 10) {
    e164 = `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith("1")) {
    e164 = `+${digits}`;
  }

  // Unparseable (7-digit local, an odd legacy string): keep it exactly as given.
  if (!e164) return value;
  return ext ? `${e164} ${ext}` : e164;
}

/** Renders a stored value for reading. Anything we can't parse comes back as-is. */
export function formatPhoneForDisplay(
  stored: string | null | undefined,
): string {
  const value = (stored ?? "").trim();
  if (!value) return "";

  const digits = digitsOf(value);
  const isUs =
    (value.startsWith("+1") && digits.length === 11) ||
    (!value.startsWith("+") && digits.length === 10);
  if (!isUs) return value;

  const local = digits.slice(-10);
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

interface PhoneSchemaOptions {
  /**
   * A value that must always be accepted — pass the value the field was loaded
   * with. Editing an existing profile is then never held hostage to a rule that
   * postdates the data it was created under.
   */
  allow?: string | null;
  /** Override the invalid-format message. */
  message?: string;
  /** Override the "this is required" message (required schema only). */
  requiredMessage?: string;
}

const isAccepted = (value: string, allow?: string | null): boolean =>
  value === (allow ?? "").trim() || isPlausiblePhone(value);

/** Optional phone field: blank is fine, anything present must be plausible. */
export function optionalPhoneSchema({ allow, message }: PhoneSchemaOptions = {}) {
  return z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine((v) => !v || isAccepted(v, allow), message ?? PHONE_ERROR_MESSAGE);
}

/** Required phone field. */
export function requiredPhoneSchema({
  allow,
  message,
  requiredMessage,
}: PhoneSchemaOptions = {}) {
  return z
    .string()
    .trim()
    .min(1, requiredMessage ?? "Phone number is required")
    .refine((v) => isAccepted(v, allow), message ?? PHONE_ERROR_MESSAGE);
}
